/**
 * HubRail — the left nav rail (CD-12 / 4A).
 *
 * A faithful port of the Claude Design prototype `nav-rail/rail.jsx`, wired to
 * the real app: nine DRILL sections (a lead row → grouped flyout), an
 * always-expanded PINNED zone (server-backed favorites), a footer and profile.
 * Favorite anything (a flyout item) → the full row lifts into Pinned AND a
 * small grayed "reflection" sits at the top of its origin flyout to unfavorite.
 * Sections and favorites are drag-reorderable (persisted per-user). Collapse
 * 264 ↔ 72. Ground is neutral Carbon (`--rail-*` tokens); the sepia app-mode
 * swaps the rail warm automatically via the theme. Paprika is reserved for one
 * at-risk signal. One unique glyph per concept.
 */
import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { RIC, RailIcon, type RailIconKey } from './railIcons';
import {
  RAIL_ITEMS, RAIL_SECTIONS, RAIL_FOOTER, sectionById, drillItemIds, drillCount,
  railItemForPath, resolveNavPinId, DEFAULT_SECTION_ORDER,
  type RailItem, type RailSection, type RailBadge, type NavGateContext,
} from './hubRailData';
import { useRailPins, useRailLayout, saveRailLayout, pinSurface, unpinSurface, type RailPin } from '../../hooks/useRailPins';
import { debugService } from '../../services/debugService';
import styles from './HubRail.module.css';
import { PIcon } from '../v2/icons';
import { Diamond as V2Diamond } from '../v2/Diamond';

const HEAD = 'var(--font-head)';
const EXP_W = 264;
const COL_W = 72;

export interface HubRailProps {
  /** Required for the STAFF rail (its gates read the permission vocabulary).
   *  Optional only because the injected/flat mode below has no gated rows. */
  navCtx?: NavGateContext;
  /** location.pathname — drives the active-row highlight. */
  activePath: string;
  /** SPA navigation (a plain left-click); modified clicks fall through to the native <a>. */
  onNavigate: (path: string) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  badges?: Partial<Record<RailBadge, number>>;
  user?: { initials: string; name: string; role: string; email?: string };
  onLogout?: () => void;
  /** FR7/FR8: the footer's What's New and Submit a Request rows open dialogs,
   *  not routes. AppShell owns the dialog state and passes these openers. */
  onWhatsNew?: () => void;
  onFeatureRequest?: () => void;

  /* ── injected (flat) mode — the client portal, free-app round 1 ──────────
   *
   * Five optional props and nothing else changes: with none of them supplied
   * the staff rail renders exactly as it did (same component, same tree), so
   * the AppShell call site is untouched. Supplying `primary` switches the rail
   * to the flat CD-12 grammar the portal's design asks for — no Pinned zone,
   * no drill flyouts, no favourites, no Dev Mode.
   */
  /** Flat rows, in order. When present, the rail renders THESE and skips
   *  sections / drills / Pinned / Dev Mode. */
  primary?: Array<RailItem | RailGroupHead>;
  /** Footer rows (Settings, Sign out). Replaces RAIL_FOOTER when present.
   *  A row whose `path` starts with '#' is an ACTION, not a route — it renders
   *  as a button and calls `onNavigate` with that token (e.g. '#signout'). */
  footer?: RailItem[];
  /** Brand block override: the mark + a title line + a small line (the
   *  workspace name). Defaults to the staff block. */
  brand?: { title: string; sub?: string; markSrc?: string };
  /** false ⇒ no Pinned zone and NO `/api/rail-pins` call at all. A portal user
   *  has no staff session, so those fetches would 401 on every render — which
   *  is why the flat branch is a SEPARATE component below rather than a
   *  conditional inside the staff one: a hook cannot be un-called. */
  pins?: false;
  /** Active-row resolution when `primary` is supplied (`railItemForPath` knows
   *  only the staff taxonomy). Returns a `RailItem.id`. */
  resolveActive?: (pathname: string) => string | null;
  /** Collapsed with no way back — the Playbook canvas (D-decision 5). The
   *  toggle is not rendered and the brand mark does not expand. */
  lockCollapsed?: boolean;
  /** Rendered as a normal flex child (`position: relative; height: 100%`)
   *  instead of the staff rail's fixed full-window aside. The portal shell
   *  puts a 36px staff-session band ABOVE the rail, so a rail pinned to the
   *  top of the window would slide under it. */
  inFlow?: boolean;

  /* ── Portal V2 (lane P1) — the rail as `shell.jsx` draws it ───────────────
   *
   * Opt-in, and only with `primary`: `voice="v2"` renders `V2FlatRail` below
   * (design `shell.jsx` Rail / Row / Profile, 2026-09-14). Without it the flat
   * rail and the staff rail render exactly as before.
   */
  voice?: 'v2';
  /** V2: a count on a row, by item id (the portal's Workflows and SOPs), or a
   *  short fraction (the assessment prep's "4/6"). */
  itemBadges?: Record<string, number | string | null | undefined>;
  /** V2: this rail sits in the phone drawer — the collapse control becomes Close. */
  phone?: boolean;
  onClosePhone?: () => void;
  /** V2: the profile block is a menu (Settings · Keyboard shortcuts · Sign out). */
  onProfileAction?: (action: 'settings' | 'shortcuts' | 'signout') => void;
}

/** A group head inside a flat rail (V2's `{ head: 'Coming' }`). */
export interface RailGroupHead { head: string }
const isRailHead = (it: RailItem | RailGroupHead): it is RailGroupHead => 'head' in it;

// A resolved pinned/surface descriptor for the Pinned zone.
interface PinDescriptor {
  key: string;
  itemId?: string;         // set when the pin is a nav tool
  isSurface: boolean;
  label: string;
  sub?: string;
  iconKey: RailIconKey;
  path: string;
  alert?: boolean;
  pin?: RailPin;           // set for deep surfaces (unfavorite by type+id)
}

const SURFACE_KIND: Record<string, { kind: string; icon: RailIconKey }> = {
  pipeline: { kind: 'Pipeline', icon: 'pipeline' },
  campaign: { kind: 'Campaign', icon: 'campaigns' },
  board: { kind: 'Board', icon: 'projects' },
  client: { kind: 'Client', icon: 'clients' },
};

function surfacePath(pin: RailPin): string {
  switch (pin.surface_type) {
    // BSG Deals c4: the per-pipeline board mount is gone — the board renders
    // the sales pipeline at bare /sales, so a pinned pipeline opens it there.
    case 'pipeline': return '/sales';
    case 'campaign': return `/sales/campaigns/${pin.surface_id}`;
    case 'board': return /^\d+$/.test(pin.surface_id) ? `/operations/projects/${pin.surface_id}` : '/operations/projects';
    case 'client': return `/clients/${pin.surface_id}`;
    default: return '/dashboard';
  }
}

function reorder<T>(list: T[], fromKey: T, toKey: T, before: boolean): T[] {
  if (fromKey === toKey) return list;
  const a = list.filter((x) => x !== fromKey);
  let idx = a.indexOf(toKey);
  if (idx < 0) return list;
  if (!before) idx += 1;
  a.splice(idx, 0, fromKey);
  return a;
}

/** Order `ids` by `order` (known first, in order), appending unknowns stably. */
function applyOrder(ids: string[], order: string[]): string[] {
  const rank = (id: string) => { const i = order.indexOf(id); return i === -1 ? Number.POSITIVE_INFINITY : i; };
  return ids.map((id, i) => ({ id, i })).sort((x, y) => { const r = rank(x.id) - rank(y.id); return r === 0 ? x.i - y.i : r; }).map((x) => x.id);
}

// ── count badge (neutral — never paprika) ──
function Badge({ n, collapsed, active }: { n?: number; collapsed?: boolean; active?: boolean }) {
  if (n == null || n === 0) return null;
  if (collapsed) {
    return <span style={{ position: 'absolute', top: 9, right: 13, minWidth: 6, height: 6, borderRadius: 4, background: 'var(--rail-active)', boxShadow: '0 0 0 2px var(--rail-ground)' }} />;
  }
  return (
    <span style={{ minWidth: 19, height: 17, padding: '0 6px', borderRadius: 4, flex: 'none',
      background: active ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.09)', color: active ? 'rgba(255,255,255,1)' : 'var(--rail-text)',
      fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.01em', display: 'grid', placeItems: 'center', fontVariantNumeric: 'tabular-nums' }}>{n > 99 ? '99+' : n}</span>
  );
}

// ── star affordance ──
function StarBtn({ favorited, show, onClick, title }: { favorited: boolean; show?: boolean; onClick: () => void; title?: string }) {
  return (
    <span role="button" tabIndex={0} title={title || (favorited ? 'Remove from favorites' : 'Favorite')}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClick(); } }}
      style={{ flex: 'none', width: 24, height: 24, borderRadius: 4, cursor: 'pointer', display: 'grid', placeItems: 'center', opacity: favorited || show ? 1 : 0, transition: 'opacity 120ms' }}>
      <RailIcon d={RIC.star} size={15} color={favorited ? 'var(--rail-active)' : 'var(--rail-faint)'} sw={favorited ? 0.5 : 1.6} style={favorited ? { fill: 'var(--rail-active)' } : undefined} />
    </span>
  );
}

// ── favorited "reflection": grayed version at the top of its origin flyout ──
function FavChip({ item, onUnfav }: { item: RailItem; onUnfav: (id: string) => void }) {
  const [h, setH] = useState(false);
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '4px 11px', borderRadius: 6, background: h ? 'var(--rail-hover)' : 'transparent', cursor: 'default' }}>
      <span style={{ flex: 'none', width: 18, display: 'grid', placeItems: 'center' }}><RailIcon d={RIC[item.iconKey]} size={13.5} color="var(--rail-ghost)" /></span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: HEAD, fontWeight: 600, fontSize: 11, color: 'var(--rail-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
      <StarBtn favorited show onClick={() => onUnfav(item.id)} />
    </div>
  );
}

interface RowProps {
  iconKey: RailIconKey; label: string; sub?: string; path?: string; badge?: number;
  active: boolean; collapsed: boolean; soon?: boolean; alert?: boolean; isSurface?: boolean; delay: number;
  canFav?: boolean; favorited?: boolean; onNav?: () => void; onToggleFav?: () => void;
  onHoverLabel?: (label: string | null, el?: HTMLElement, soon?: boolean) => void;
  /** A `soon` row that still NAVIGATES — the portal's `· soon` rows open a
   *  teaser page. Staff `soon` rows stay inert (no destination exists yet). */
  soonNavigable?: boolean;
  /** The coming-soon chip's copy + frame. Defaults to the rail's own solid
   *  "Soon"; the portal passes the app-wide dashed "· soon" grammar. */
  soonText?: string; soonDashed?: boolean;
  draggable?: boolean; grip?: boolean;
  onDragStart?: (e: React.DragEvent) => void; onDragEnter?: (e: React.DragEvent) => void; onDragEnd?: () => void;
  dropLine?: 'before' | 'after' | null;
}

// ── one rail row (favorite target, pinned row, footer) ──
function Row(props: RowProps) {
  const { iconKey, label, sub, path, badge, active, collapsed, soon, alert, isSurface, delay,
    canFav, favorited, onNav, onToggleFav, onHoverLabel, soonNavigable, soonText, soonDashed,
    draggable, grip, onDragStart, onDragEnter, onDragEnd, dropLine } = props;
  const [h, setH] = useState(false);
  const bg = active ? 'var(--rail-active-tint)' : h ? 'var(--rail-hover)' : 'transparent';
  const labelColor = active ? 'var(--rail-hi)' : soon ? 'var(--rail-ghost)' : h ? 'var(--rail-hi)' : 'var(--rail-text)';
  const iconColor = active ? 'var(--rail-active)' : soon ? 'var(--rail-ghost)' : h ? 'var(--rail-hi)' : 'var(--rail-icon)';
  const clickable = (!soon || !!soonNavigable) && !!onNav;
  const onClick = (e: React.MouseEvent) => {
    if (!clickable) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e as React.MouseEvent).button !== 0) return; // let native <a> handle new-tab
    e.preventDefault();
    onNav!();
  };
  const inner = (
    <>
      <span className={styles.ico} style={{ flex: 'none', position: 'relative', display: 'grid', placeItems: 'center', width: 22, height: 22 }}>
        <RailIcon d={RIC[iconKey]} size={18.5} color={iconColor} />
        {isSurface && <span style={{ position: 'absolute', right: -1, bottom: -1, width: 6, height: 6, transform: 'rotate(45deg)', background: alert ? 'var(--rail-paprika)' : 'var(--rail-active)', boxShadow: '0 0 0 1.5px var(--rail-ground)' }} />}
      </span>
      {!collapsed && (
        <span style={{ flex: 1, minWidth: 0, lineHeight: isSurface ? 1.25 : 1.2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontFamily: HEAD, fontWeight: isSurface ? 600 : 550, fontSize: 13.5, letterSpacing: '.005em', color: labelColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            {soon && <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--rail-faint)', border: `1px ${soonDashed ? 'dashed' : 'solid'} var(--rail-hair)`, borderRadius: 3, padding: '1px 4px', flex: 'none' }}>{soonText ?? 'Soon'}</span>}
          </span>
          {isSurface && sub && <span style={{ display: 'block', fontSize: 10.5, color: 'var(--rail-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>{sub}</span>}
        </span>
      )}
      {!collapsed && !soon && badge != null && <Badge n={badge} active={active} />}
      {collapsed && badge != null && <Badge n={badge} active={active} collapsed />}
      {!collapsed && grip && (
        <span className="hubrail-grip" style={{ flex: 'none', display: 'grid', placeItems: 'center', width: 14, opacity: h ? 0.55 : 0, cursor: 'grab', transition: 'opacity 120ms' }}>
          <RailIcon d={RIC.grip} size={14} color="var(--rail-hi)" />
        </span>
      )}
      {!collapsed && canFav && !grip && <StarBtn favorited={!!favorited} show={h} onClick={() => onToggleFav && onToggleFav()} />}
    </>
  );
  const commonStyle: React.CSSProperties = {
    position: 'relative', display: 'flex', alignItems: 'center', gap: 11, width: '100%',
    padding: collapsed ? '8px 0' : (isSurface ? '7px 10px 7px 11px' : '8px 11px'),
    justifyContent: collapsed ? 'center' : 'flex-start', background: bg, border: 'none', borderRadius: 6,
    cursor: soon ? 'default' : 'pointer', textAlign: 'left', transition: 'background 110ms', minWidth: 0, textDecoration: 'none',
  };
  return (
    <div className={soon ? styles.railIn : `${styles.railIn} ${styles.tiltOwner}`} style={{ position: 'relative', animationDelay: `${delay}ms` }}
      onMouseEnter={(e) => { setH(true); if (collapsed && onHoverLabel) onHoverLabel(label, e.currentTarget, soon); }}
      onMouseLeave={() => { setH(false); if (collapsed && onHoverLabel) onHoverLabel(null); }}>
      {dropLine && <span style={{ position: 'absolute', left: 8, right: 8, top: dropLine === 'before' ? -2 : 'auto', bottom: dropLine === 'after' ? -2 : 'auto', height: 2, background: 'var(--rail-active)', borderRadius: '6px', zIndex: 2 }} />}
      {active && <span style={{ position: 'absolute', left: collapsed ? 8 : 2, top: '50%', transform: 'translateY(-50%)', width: 3, height: collapsed ? 22 : 18, borderRadius: 3, background: 'var(--rail-active)' }} />}
      {clickable && path
        ? <a href={path} draggable={false} onClick={onClick} onDragStart={draggable ? onDragStart : undefined} onDragEnter={onDragEnter} onDragOver={draggable ? (e) => e.preventDefault() : undefined} onDragEnd={onDragEnd} style={commonStyle}>{inner}</a>
        : <button type="button" draggable={draggable} onDragStart={onDragStart} onDragEnter={onDragEnter} onDragOver={draggable ? (e) => e.preventDefault() : undefined} onDragEnd={onDragEnd} onClick={onClick} style={commonStyle}>{inner}</button>}
    </div>
  );
}

// ── the "Pinned" kicker (also the collapsed group divider) ──
function PinnedLabel({ collapsed, delay }: { collapsed: boolean; delay: number }) {
  if (collapsed) return (
    <div className={styles.railIn} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 15px 6px', animationDelay: `${delay}ms` }}>
      <span style={{ flex: 1, height: 1, background: 'var(--rail-hair-soft)' }} /><RailIcon d={RIC.secPinned} size={15} color="var(--rail-ghost)" /><span style={{ flex: 1, height: 1, background: 'var(--rail-hair-soft)' }} />
    </div>
  );
  return (
    <div className={styles.railIn} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '15px 12px 5px 14px', animationDelay: `${delay}ms` }}>
      <RailIcon d={RIC.secPinned} size={13} color="var(--rail-faint)" style={{ flex: 'none' }} />
      <span style={{ flex: 1, fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--rail-faint)' }}>Pinned</span>
    </div>
  );
}

// ── drill-down lead row (opens the flyout; also a section drag handle) ──
interface DrillRowProps {
  section: RailSection; open: boolean; activeInside: boolean; collapsed: boolean; delay: number; count: number;
  onOpen: (id: string, el: HTMLElement) => void; onHoverLabel: (label: string | null, el?: HTMLElement) => void;
  secDraggable: boolean; onSecDragStart: (e: React.DragEvent) => void; onSecDragEnter: (e: React.DragEvent) => void; onSecDragEnd: () => void; secDrop: 'before' | 'after' | null;
}
function DrillRow({ section, open, activeInside, collapsed, delay, count, onOpen, onHoverLabel, secDraggable, onSecDragStart, onSecDragEnter, onSecDragEnd, secDrop }: DrillRowProps) {
  const [h, setH] = useState(false);
  const hot = open || h;
  return (
    <div data-drill={section.id} className={`${styles.railIn} ${styles.tiltOwner}`} draggable={secDraggable && !collapsed} onDragStart={onSecDragStart} onDragEnter={onSecDragEnter} onDragOver={secDraggable ? (e) => e.preventDefault() : undefined} onDragEnd={onSecDragEnd}
      style={{ position: 'relative', marginTop: 4, animationDelay: `${delay}ms` }}
      onMouseEnter={(e) => { setH(true); if (collapsed) onHoverLabel(section.label, e.currentTarget); }}
      onMouseLeave={() => { setH(false); if (collapsed) onHoverLabel(null); }}>
      {secDrop && <span style={{ position: 'absolute', left: 8, right: 8, top: secDrop === 'before' ? -2 : 'auto', bottom: secDrop === 'after' ? -2 : 'auto', height: 2, background: 'var(--rail-active)', borderRadius: '6px', zIndex: 2 }} />}
      {(open || activeInside) && <span style={{ position: 'absolute', left: collapsed ? 8 : 2, top: '50%', transform: 'translateY(-50%)', width: 3, height: collapsed ? 22 : 18, borderRadius: 3, background: 'var(--rail-active)' }} />}
      <button type="button" onClick={(e) => onOpen(section.id, e.currentTarget)}
        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: collapsed ? '9px 0' : '9px 11px', justifyContent: collapsed ? 'center' : 'flex-start', background: open ? 'rgba(255,255,255,0.07)' : h ? 'var(--rail-hover)' : 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', textAlign: 'left', transition: 'background 110ms' }}>
        <span className={styles.ico} style={{ flex: 'none', position: 'relative', display: 'grid', placeItems: 'center', width: 22, height: 22 }}>
          <RailIcon d={RIC[section.iconKey]} size={19} color={hot || activeInside ? 'var(--rail-active)' : 'var(--rail-icon)'} />
          {activeInside && !open && <span style={{ position: 'absolute', right: -1, bottom: -1, width: 6, height: 6, transform: 'rotate(45deg)', background: 'var(--rail-active)', boxShadow: '0 0 0 1.5px var(--rail-ground)' }} />}
        </span>
        {!collapsed && <span style={{ flex: 1, fontFamily: HEAD, fontWeight: 700, fontSize: 13.5, letterSpacing: '.005em', color: hot ? 'var(--rail-hi)' : 'var(--rail-text)', whiteSpace: 'nowrap' }}>{section.label}</span>}
        {!collapsed && <span style={{ flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.11em', textTransform: 'uppercase', color: 'var(--rail-ghost)' }}>{count} tools</span>
          <RailIcon d={RIC.chevRight} size={15} color={hot ? 'var(--rail-active)' : 'var(--rail-ghost)'} />
        </span>}
      </button>
    </div>
  );
}

// ── one flyout item row ──
function FlyItem({ it, on, onNav, onToggleFav }: { it: RailItem; on: boolean; onNav: () => void; onToggleFav: () => void }) {
  const [h, setH] = useState(false);
  const onClick = (e: React.MouseEvent) => {
    if (it.soon) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault(); onNav();
  };
  const body = (
    <>
      <span className={styles.ico} style={{ flex: 'none', width: 28, height: 28, borderRadius: '4px', display: 'grid', placeItems: 'center', background: on ? 'var(--rail-active-tint)' : 'rgba(255,255,255,0.05)' }}><RailIcon d={RIC[it.iconKey]} size={16} color={it.soon ? 'var(--rail-ghost)' : on ? 'var(--rail-active)' : 'var(--rail-text)'} /></span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, color: it.soon ? 'var(--rail-ghost)' : on ? 'var(--rail-hi)' : 'var(--rail-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
      {/* Portal V2 (HANDOFF §5 Q5): the staff rail's coming-soon chip converges on
          the dashed "· soon" grammar the portal rail uses (shell.jsx:28). */}
      {it.soon ? <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--rail-faint)', border: '1px dashed var(--rail-hair)', borderRadius: 3, padding: '1px 4px', flex: 'none' }}>· soon</span>
        : <StarBtn favorited={false} show={h} onClick={onToggleFav} title="Favorite" />}
    </>
  );
  const style: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '7px 9px', borderRadius: 5, cursor: it.soon ? 'default' : 'pointer', background: on ? 'var(--rail-active-tint)' : h ? 'var(--rail-hover)' : 'transparent', textDecoration: 'none', border: 'none', textAlign: 'left' };
  return (
    <div className={it.soon ? undefined : styles.tiltOwner} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>
      {it.soon
        ? <div style={style}>{body}</div>
        : <a href={it.path} draggable={false} onClick={onClick} style={style}>{body}</a>}
    </div>
  );
}

// ── drill-down flyout sub-panel ──
interface FlyoutProps {
  section: RailSection; favHere: string[]; pos: number; railW: number; activePath: string; favSet: Set<string>; navCtx: NavGateContext;
  onNav: (it: RailItem) => void; onClose: () => void; onToggleFav: (id: string) => void;
}
function DrillFlyout({ section, favHere, pos, railW, activePath, favSet, navCtx, onNav, onClose, onToggleFav }: FlyoutProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current && !ref.current.contains(t) && !t.closest('[data-drill]')) onClose();
    };
    document.addEventListener('keydown', onKey); document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [onClose]);
  // Open beside the section row, then MEASURE the panel and slide its top edge
  // up until the whole list fits above the bottom of the window — a taller
  // section (EOS, 11 tools) opens further up rather than asking for a scroll.
  // Only a window shorter than the list itself falls back to scrolling.
  const vh = window.innerHeight, maxH = vh - 24;
  const [top, setTop] = useState(() => Math.max(12, Math.min(pos, vh - 12 - Math.min(maxH, 560))));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    setTop(Math.max(12, Math.min(pos, window.innerHeight - 12 - h)));
  }, [pos, section.id]);
  const active = railItemForPath(activePath, window.location.search);
  return (
    <div ref={ref} data-flyout className={styles.flyout} style={{ position: 'fixed', left: railW + 6, top, width: 268, maxHeight: maxH, zIndex: 1400, background: 'var(--rail-lift)', border: '1px solid var(--rail-hair)', borderRadius: '6px', boxShadow: 'var(--modal)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '14px 15px 12px', borderBottom: '1px solid var(--rail-hair)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ flex: 'none', width: 30, height: 30, borderRadius: 6, background: 'var(--rail-active-tint)', display: 'grid', placeItems: 'center' }}><RailIcon d={RIC[section.iconKey]} size={17} color="var(--rail-active)" /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 14, color: 'var(--rail-hi)', letterSpacing: '-.01em' }}>{section.label}</div>
          <div style={{ fontSize: 10.5, color: 'var(--rail-faint)', marginTop: 1, lineHeight: 1.35 }}>{section.blurb}</div>
        </div>
        <button type="button" onClick={onClose} title="Close" style={{ flex: 'none', width: 24, height: 24, borderRadius: 4, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><RailIcon d={RIC.close} size={14} color="var(--rail-faint)" /></button>
      </div>
      <div className={styles.nav} style={{ overflowY: 'auto', padding: '7px 8px 10px' }}>
        {favHere.length > 0 && (
          <div style={{ marginBottom: 4, paddingBottom: 6, borderBottom: '1px solid var(--rail-hair-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '3px 8px 5px' }}>
              <RailIcon d={RIC.star} size={11} color="var(--rail-active)" sw={0.5} style={{ fill: 'var(--rail-active)' }} />
              <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.13em', textTransform: 'uppercase', color: 'var(--rail-faint)' }}>Favorited</span>
            </div>
            {favHere.map((id) => { const it = RAIL_ITEMS[id]; return it ? <FavChip key={id} item={it} onUnfav={onToggleFav} /> : null; })}
          </div>
        )}
        {section.groups.map((g, gi) => {
          const items = g.items.filter((id) => !favSet.has(id) && RAIL_ITEMS[id] && RAIL_ITEMS[id].visible(navCtx));
          if (!items.length) return null;
          return (
            <div key={g.head} style={{ marginTop: gi ? 9 : 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '4px 8px 5px' }}>
                <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.13em', textTransform: 'uppercase', color: g.soon ? 'var(--rail-ghost)' : 'var(--rail-faint)' }}>{g.head}</span>
                <span style={{ flex: 1, height: 1, background: 'var(--rail-hair-soft)' }} />
              </div>
              {items.map((id) => { const it = RAIL_ITEMS[id]; return <FlyItem key={id} it={it} on={active?.path === it.path} onNav={() => onNav(it)} onToggleFav={() => onToggleFav(it.id)} />; })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BrandMark({ src }: { src?: string } = {}) {
  const [ok, setOk] = useState(true);
  if (!ok) return <span style={{ width: 34, height: 34, borderRadius: '6px', background: 'var(--indigo)', display: 'grid', placeItems: 'center', flex: 'none' }}><span style={{ width: 12, height: 12, transform: 'rotate(45deg)', background: 'rgba(255,255,255,1)' }} /></span>;
  return <img src={`${process.env.PUBLIC_URL || ''}${src ?? '/logo-icon-white.png'}`} alt="Black Sheep Global" onError={() => setOk(false)} style={{ width: 34, height: 34, borderRadius: '6px', flex: 'none', display: 'block' }} />;
}

/**
 * ── the FLAT rail (injected mode) ──────────────────────────────────────────
 *
 * The client portal's rail: the same CD-12 grammar, the same `Row`, the same
 * Carbon ground and the same 264↔72 collapse — with the staff-only machinery
 * (Pinned, drill flyouts, favourites, Dev Mode) simply absent rather than
 * hidden. It is a SEPARATE component, not a branch inside the staff rail,
 * because `useRailPins()` / `useRailLayout()` fetch `/api/rail-pins` on mount
 * and a portal visitor has no staff session: a conditional cannot un-call a
 * hook, but an unmounted component never calls one. That is the `pins={false}`
 * guarantee — zero network calls to `/api/rail-pins` from `/portal`.
 */
function FlatRail({
  primary, footer, brand, activePath, resolveActive, onNavigate, collapsed, onToggleCollapse,
  user, lockCollapsed, inFlow,
}: HubRailProps & { primary: Array<RailItem | RailGroupHead> }) {
  const [tip, setTip] = useState<{ text: string; top: number } | null>(null);
  const width = collapsed ? COL_W : EXP_W;
  const activeId = resolveActive ? resolveActive(activePath) : null;
  const hoverLabel = (text: string | null, el?: HTMLElement) => {
    if (!text || !el) { setTip(null); return; }
    const r = el.getBoundingClientRect();
    setTip({ text, top: r.top + r.height / 2 });
  };
  let d = 0; const nd = () => Math.min(d++ * 14, 300);

  // An action row ('#signout') has no destination — Row renders a <button>
  // when `path` is undefined, which is also the right element for it.
  const renderRow = (it: RailItem) => {
    const isAction = it.path.startsWith('#');
    return (
      <Row key={it.id} iconKey={it.iconKey} label={it.label} path={isAction ? undefined : it.path}
        active={activeId === it.id} collapsed={!!collapsed} soon={it.soon} soonNavigable soonText="· soon" soonDashed
        onNav={() => onNavigate(it.path)} onHoverLabel={hoverLabel} delay={nd()} />
    );
  };

  const aside: React.CSSProperties = inFlow
    ? { position: 'relative', height: '100%' }
    : { position: 'fixed', top: 0, left: 0, height: '100vh' };

  return (
    <aside style={{ ...aside, width, flex: 'none', background: 'var(--rail-ground)', boxShadow: 'inset -1px 0 0 rgba(0,0,0,.5), 8px 0 30px -20px rgba(0,0,0,.5)', zIndex: 1201, transition: 'width 170ms cubic-bezier(.2,.7,.3,1)', display: 'flex', flexDirection: 'column' }}>
      {/* brand: the mark + "The Process Lounge" + the workspace name */}
      <div className={styles.railIn} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: collapsed ? '17px 0 15px' : '18px 12px 16px 15px', justifyContent: collapsed ? 'center' : 'flex-start', borderBottom: '1px solid var(--rail-hair)' }}>
        <button type="button" onClick={collapsed && !lockCollapsed ? onToggleCollapse : undefined} title={collapsed && !lockCollapsed ? 'Expand rail' : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 0, background: 'transparent', border: 'none', cursor: collapsed && !lockCollapsed ? 'pointer' : 'default', minWidth: 0, flex: collapsed ? 'none' : 1 }}>
          <BrandMark src={brand?.markSrc} />
          {!collapsed && (
            <span style={{ flex: 1, minWidth: 0, lineHeight: 1.15, textAlign: 'left' }}>
              <span style={{ display: 'block', fontFamily: HEAD, fontWeight: 800, fontSize: 14, letterSpacing: '-.01em', color: 'var(--rail-hi)', whiteSpace: 'nowrap' }}>{brand?.title ?? 'The Process Lounge'}</span>
              {brand?.sub && <span style={{ display: 'block', fontSize: 10.5, color: 'var(--rail-faint)', letterSpacing: '.02em', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{brand.sub}</span>}
            </span>
          )}
        </button>
        {!collapsed && !lockCollapsed && (
          <button type="button" onClick={onToggleCollapse} title="Collapse rail" style={{ flex: 'none', width: 30, height: 30, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <RailIcon d={RIC.collapse} size={17} color="var(--rail-faint)" />
          </button>
        )}
      </div>

      <nav className={styles.nav} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: collapsed ? '10px 12px 12px' : '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {primary.filter((it): it is RailItem => !isRailHead(it)).map(renderRow)}
      </nav>

      {footer && footer.length > 0 && (
        <div style={{ padding: collapsed ? '8px 12px' : '6px 12px 8px', borderTop: '1px solid var(--rail-hair)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {footer.map(renderRow)}
        </div>
      )}

      {/* profile — display name + "{workspace} · {role}". Sign out lives in the
          footer here, so this block carries no second exit. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '10px 0' : '10px 14px', justifyContent: collapsed ? 'center' : 'flex-start', borderTop: '1px solid var(--rail-hair)' }}>
        <div style={{ width: 32, height: 32, borderRadius: '4px', background: 'var(--indigo)', color: 'rgba(255,255,255,1)', display: 'grid', placeItems: 'center', fontFamily: HEAD, fontWeight: 700, fontSize: 12.5, flex: 'none' }}>{user?.initials ?? '?'}</div>
        {!collapsed && (
          <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
            <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, color: 'var(--rail-hi)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name ?? ''}</div>
            <div style={{ fontSize: 10.5, color: 'var(--rail-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.role ?? ''}</div>
          </div>
        )}
      </div>

      {collapsed && tip && (
        <div style={{ position: 'fixed', left: COL_W + 8, top: tip.top, transform: 'translateY(-50%)', zIndex: 1400, background: 'var(--rail-lift)', color: 'var(--rail-hi)', border: '1px solid var(--rail-hair)', borderRadius: 6, padding: '6px 11px', fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap', boxShadow: 'var(--pop)', pointerEvents: 'none' }}>
          {tip.text}
        </div>
      )}
    </aside>
  );
}

/**
 * ── the V2 portal rail (`voice="v2"`, lane P1) ──────────────────────────────
 *
 * Design `portal-free-app-v2/shell.jsx` — `Rail` (:76-106), `Row` (:17-42) and
 * `Profile` (:44-74) — line for line. Every size, padding, colour and motion is
 * the design's; the carbon palette `P` (:9) reads the V2 mode-invariant tokens
 * (K1A, `theme/cssVariables.ts`). The motion classes are the `v2-` prefixed
 * ones, so this rail animates only inside a `V2Root` (the portal shell is one).
 *
 * Two places differ from the prototype's DOM, never from its look:
 *   • the collapsed hover flyout is `position: fixed` at the rail level — the
 *     design's `left: 100%` child of a row sits inside `nav`'s `overflow-y:
 *     auto`, which computes overflow-x to auto and clips it (CSS Overflow 3);
 *   • a modified click (⌘/Ctrl/middle) on a row opens the section in a new tab.
 */
const V2P = {
  RB: 'var(--v2-carbon)',
  FLY: 'var(--v2-carbon-lift)',
  HAIR: 'var(--v2-carbon-hair)',
  /* shell.jsx:9 HAIRs — no V2 token carries .05; an rgba literal, as the design writes it */
  HAIRs: 'rgba(255,255,255,.05)',
  TXT: 'var(--v2-on-carbon-soft)',
  TXTHI: 'var(--v2-on-carbon)',
  FAINT: 'var(--v2-on-carbon-faint)',
  GHOST: 'var(--v2-on-carbon-ghost)',
  ICO: 'var(--v2-on-carbon-icon)',
  HOV: 'var(--v2-carbon-hover)',
  INDHI: 'var(--v2-carbon-indigo)',
  INDT: 'var(--v2-carbon-indigo-tint)',
} as const;

type V2Fly = { label: string; soon: boolean; left: number; top: number } | null;

/** shell.jsx:12-16 — the 32px mark, with the indigo square + white diamond when the image fails. */
function V2BrandMark({ src }: { src?: string }) {
  const [ok, setOk] = useState(true);
  if (!ok) return <span style={{ width: 32, height: 32, borderRadius: '6px', background: 'var(--indigo)', display: 'grid', placeItems: 'center', flex: 'none' }}><V2Diamond size={12} color="var(--on-brand)" /></span>;
  return <img src={`${process.env.PUBLIC_URL || ''}${src ?? '/logo-icon-white.png'}`} alt="Black Sheep Global" onError={() => setOk(false)} style={{ width: 32, height: 32, borderRadius: '6px', flex: 'none', display: 'block' }} />;
}

/** shell.jsx:17-42 — one row. */
function V2RailRow({ item, active, collapsed, delay, badge, onNav, onFly }: {
  item: RailItem; active: boolean; collapsed: boolean; delay: number; badge?: number | string | null;
  onNav: (item: RailItem) => void; onFly: (fly: V2Fly) => void;
}) {
  const [h, setH] = useState(false);
  const soon = !!item.soon;
  const openInNewTab = (e: React.MouseEvent) => {
    if (item.path.startsWith('#')) return false;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) { window.open(item.path, '_blank', 'noopener'); return true; }
    return false;
  };
  return (
    <div className={soon ? 'v2-fadeUp' : `v2-fadeUp ${styles.tiltOwner}`} style={{ position: 'relative', animationDelay: `${delay}ms` }}
      onMouseEnter={(e) => {
        setH(true);
        if (collapsed) { const r = e.currentTarget.getBoundingClientRect(); onFly({ label: item.label, soon, left: r.right + 12, top: r.top + r.height / 2 }); }
      }}
      onMouseLeave={() => { setH(false); if (collapsed) onFly(null); }}>
      {active && <span style={{ position: 'absolute', left: collapsed ? 8 : 2, top: '50%', transform: 'translateY(-50%)', width: 3, height: collapsed ? 22 : 18, borderRadius: 3, background: V2P.INDHI }} />}
      <button type="button" aria-current={active ? 'page' : undefined} aria-label={collapsed ? item.label : undefined}
        onClick={(e) => { if (!openInNewTab(e)) onNav(item); }}
        onAuxClick={(e) => { if (e.button === 1) openInNewTab(e); }}
        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 11, width: '100%', minHeight: 40, padding: collapsed ? '9px 0' : '9px 11px', justifyContent: collapsed ? 'center' : 'flex-start', background: active ? V2P.INDT : h && !soon ? V2P.HOV : 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', textAlign: 'left', minWidth: 0 }}>
        <span className={styles.ico} style={{ flex: 'none', display: 'grid', placeItems: 'center', width: 22, height: 22 }}><RailIcon d={RIC[item.iconKey]} size={18.5} color={active ? V2P.INDHI : soon ? V2P.GHOST : h ? V2P.TXTHI : V2P.ICO} /></span>
        {!collapsed && <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 13.5, color: active ? V2P.TXTHI : soon ? V2P.GHOST : h ? V2P.TXTHI : V2P.TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
          {soon && <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: V2P.FAINT, border: `1px dashed ${V2P.HAIR}`, borderRadius: 3, padding: '1px 4px', flex: 'none' }}>· soon</span>}
        </span>}
        {!collapsed && badge != null && <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, color: active ? 'var(--on-brand)' : V2P.TXT, background: active ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.09)', borderRadius: 4, padding: '1px 6px', fontVariantNumeric: 'tabular-nums' }}>{badge}</span>}
      </button>
    </div>
  );
}

/** shell.jsx:44-74 — the rail footer is one control: the profile menu. */
function V2RailProfile({ user, collapsed, onAction }: {
  user?: HubRailProps['user']; collapsed: boolean; onAction?: HubRailProps['onProfileAction'];
}) {
  const [open, setOpen] = useState(false);
  const name = user?.name || '';
  const items: Array<{ label: string; icon: 'settings' | 'terminal' | 'logout'; action: 'settings' | 'shortcuts' | 'signout' }> = [
    { label: 'Settings', icon: 'settings', action: 'settings' },
    { label: 'Keyboard shortcuts', icon: 'terminal', action: 'shortcuts' },
    { label: 'Sign out', icon: 'logout', action: 'signout' },
  ];
  return (
    <div style={{ position: 'relative', borderTop: `1px solid ${V2P.HAIR}`, padding: collapsed ? '8px 12px' : '8px 12px' }}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={collapsed ? name : undefined} onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, padding: collapsed ? '6px 0' : '6px 8px', justifyContent: collapsed ? 'center' : 'flex-start', background: open ? V2P.HOV : 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
        <span style={{ width: 30, height: 30, borderRadius: '4px', background: 'var(--indigo)', color: 'var(--on-brand)', display: 'grid', placeItems: 'center', fontFamily: HEAD, fontWeight: 700, fontSize: 12.5, flex: 'none' }}>{(name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}</span>
        {!collapsed && <span style={{ flex: 1, minWidth: 0, textAlign: 'left', lineHeight: 1.3 }}>
          <span style={{ display: 'block', fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, color: V2P.TXTHI, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
          <span style={{ display: 'block', fontSize: 10.5, color: V2P.FAINT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.role}</span>
        </span>}
        {!collapsed && <PIcon name="chevUp" size={14} color={V2P.FAINT} />}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 80 }} />
          <div className="v2-dnSpring" role="menu" style={{ position: 'absolute', left: 10, right: 10, bottom: 'calc(100% - 4px)', zIndex: 90, transformOrigin: 'bottom left', background: V2P.FLY, border: `1px solid ${V2P.HAIR}`, borderRadius: 6, padding: 5, boxShadow: 'var(--pop)', minWidth: 190 }}>
            <div style={{ padding: '7px 9px 8px', borderBottom: `1px solid ${V2P.HAIRs}`, marginBottom: 4 }}>
              <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 12.5, color: V2P.TXTHI }}>{name}</div>
              <div style={{ fontSize: 10.5, color: V2P.FAINT, marginTop: 2 }}>{user?.email}</div>
            </div>
            {items.map((it) => (
              <button key={it.label} type="button" role="menuitem" onClick={() => { setOpen(false); onAction?.(it.action); }}
                style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', minHeight: 36, textAlign: 'left', background: 'transparent', border: 'none', borderRadius: 4, padding: '8px 9px', cursor: 'pointer', fontFamily: HEAD, fontSize: 12.5, fontWeight: 600, color: V2P.TXT }}
                onMouseEnter={(e) => { e.currentTarget.style.background = V2P.HOV; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <PIcon name={it.icon} size={14} color={V2P.FAINT} />{it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * shell.jsx:76-106 — the rail. `collapsed` and `lockCollapsed` come from the
 * shell (it measures its own frame); `phone` is the copy inside the phone
 * drawer, whose top-right control is Close instead of Collapse. A locked rail
 * (the Playbook canvas, and the 900–1180 band where the frame forces 72px)
 * draws no Expand affordance.
 */
function V2FlatRail({
  primary, footer, brand, activePath, resolveActive, onNavigate, collapsed, onToggleCollapse,
  user, lockCollapsed, itemBadges, phone, onClosePhone, onProfileAction,
}: HubRailProps & { primary: Array<RailItem | RailGroupHead> }) {
  const [fly, setFly] = useState<V2Fly>(null);
  const activeId = resolveActive ? resolveActive(activePath) : null;
  const canToggle = !phone && !lockCollapsed;
  const nav = (it: RailItem) => onNavigate(it.path);
  useEffect(() => { if (!collapsed) setFly(null); }, [collapsed]);
  let d = 0; const nd = () => Math.min(d++ * 14, 280);
  return (
    <aside style={{ position: 'relative', height: '100%', width: collapsed ? COL_W : EXP_W, flex: 'none', background: V2P.RB, boxShadow: 'inset -1px 0 0 rgba(0,0,0,.5), 8px 0 30px -20px rgba(22,24,27,.5)', zIndex: 40, transition: 'width 180ms var(--e-out)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: collapsed ? '16px 0 14px' : '16px 12px 14px 15px', justifyContent: collapsed ? 'center' : 'flex-start', borderBottom: `1px solid ${V2P.HAIR}` }}>
        <button type="button" onClick={collapsed && canToggle ? onToggleCollapse : undefined} title={collapsed && canToggle ? 'Expand' : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 0, background: 'transparent', border: 'none', cursor: collapsed && canToggle ? 'pointer' : 'default', minWidth: 0, flex: collapsed ? 'none' : 1 }}>
          <V2BrandMark src={brand?.markSrc} />
          {!collapsed && <span style={{ flex: 1, minWidth: 0, lineHeight: 1.2, textAlign: 'left' }}>
            <span style={{ display: 'block', fontFamily: HEAD, fontWeight: 800, fontSize: 14, letterSpacing: '-.01em', color: V2P.TXTHI, whiteSpace: 'nowrap' }}>{brand?.title ?? 'The Process Lounge'}</span>
            <span style={{ display: 'block', fontSize: 10.5, color: V2P.FAINT, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{brand?.sub}</span>
          </span>}
        </button>
        {!collapsed && (phone || canToggle) && <button type="button" onClick={phone ? onClosePhone : onToggleCollapse} title={phone ? 'Close' : 'Collapse'} style={{ flex: 'none', width: 32, height: 32, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><PIcon name={phone ? 'close' : 'collapse'} size={17} color={V2P.FAINT} /></button>}
      </div>
      <nav className="v2-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: collapsed ? '10px 12px' : '8px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {primary.map((it) => (isRailHead(it)
          ? <div key={it.head} className="v2-fadeUp" style={{ animationDelay: `${nd()}ms`, display: 'flex', alignItems: 'center', gap: 8, padding: collapsed ? '12px 0 5px' : '14px 4px 5px' }}>
              {collapsed ? <span style={{ flex: 1, height: 1, background: V2P.HAIRs }} /> : <><span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: V2P.FAINT }}>{it.head}</span><span style={{ flex: 1, height: 1, background: V2P.HAIRs }} /></>}
            </div>
          : <V2RailRow key={it.id} item={it} active={activeId === it.id} collapsed={!!collapsed} delay={nd()} badge={itemBadges?.[it.id]} onNav={nav} onFly={setFly} />))}
      </nav>
      <div style={{ padding: collapsed ? '8px 12px' : '6px 12px 8px', borderTop: `1px solid ${V2P.HAIR}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(footer || []).map((f) => <V2RailRow key={f.id} item={f} active={activeId === f.id} collapsed={!!collapsed} delay={nd()} badge={itemBadges?.[f.id]} onNav={nav} onFly={setFly} />)}
      </div>
      <V2RailProfile user={user} collapsed={!!collapsed} onAction={onProfileAction} />
      {collapsed && fly && (
        /* the fixed wrapper carries the centring: the flyout's dnSpring keyframes end on `transform: none` */
        <div style={{ position: 'fixed', left: fly.left, top: fly.top, transform: 'translateY(-50%)', zIndex: 70, pointerEvents: 'none' }}>
          <div className="v2-dnSpring" style={{ background: V2P.FLY, color: V2P.TXTHI, border: `1px solid ${V2P.HAIR}`, borderRadius: 6, padding: '6px 11px', fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap', boxShadow: 'var(--pop)', display: 'flex', gap: 8 }}>
            {fly.label}{fly.soon && <span style={{ fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: V2P.FAINT }}>soon</span>}
          </div>
        </div>
      )}
    </aside>
  );
}

/** The one entry point. `primary` present ⇒ the flat (portal) rail; absent ⇒
 *  the staff rail, byte-for-byte the component it has always been. */
export default function HubRail(props: HubRailProps) {
  if (props.primary && props.voice === 'v2') return <V2FlatRail {...props} primary={props.primary} />;
  if (props.primary) return <FlatRail {...props} primary={props.primary} />;
  return <StaffRail {...props} />;
}

// ── the rail ──
function StaffRail({ navCtx, activePath, onNavigate, collapsed, onToggleCollapse, badges, user, onLogout, onWhatsNew, onFeatureRequest }: HubRailProps) {
  // `navCtx` is optional on the shared props type only so the injected (flat)
  // rail need not fabricate a permission context. The staff branch always
  // receives one — AppShell's call site is unchanged.
  const ctx = navCtx as NavGateContext;
  const { pins: railPins } = useRailPins();
  const layout = useRailLayout();
  const [pos, setPos] = useState(0);
  const [openDrill, setOpenDrill] = useState<string | null>(null);
  // Dev Mode lives in debugService (localStorage-backed), and Ctrl+Shift+D can
  // flip it from anywhere — so mirror it into state on mount AND listen, or the
  // rail row would drift out of sync with the DEV chip in the header.
  const [devOn, setDevOn] = useState(() => debugService.isEnabled);
  useEffect(() => {
    const sync = () => setDevOn(debugService.isEnabled);
    window.addEventListener('storage', sync);
    window.addEventListener('bsg-dev-mode-changed', sync as EventListener);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('bsg-dev-mode-changed', sync as EventListener);
    };
  }, []);
  const toggleDevMode = React.useCallback(() => {
    const on = debugService.toggle();
    setDevOn(on);
    window.dispatchEvent(new Event('bsg-dev-mode-changed'));
  }, []);
  const [tip, setTip] = useState<{ text: string; top: number } | null>(null);
  const [drag, setDrag] = useState<string | null>(null);        // pinned drag key
  const [dropAt, setDropAt] = useState<{ key: string; before: boolean } | null>(null);
  const [secDrag, setSecDrag] = useState<string | null>(null);  // section drag id
  const [secDrop, setSecDrop] = useState<{ id: string; before: boolean } | null>(null);

  const width = collapsed ? COL_W : EXP_W;
  const active = railItemForPath(activePath, window.location.search);
  const isActive = (path: string) => !!active && active.path === path;

  // Close the flyout on app scroll / resize (a fixed-position panel would drift).
  useEffect(() => {
    const close = () => setOpenDrill(null);
    // Capture-phase scroll fires for EVERY scrolling element, including the
    // flyout's own tool list — a scroll inside the panel must not close it.
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (t && t instanceof Element && t.closest('[data-flyout]')) return;
      close();
    };
    window.addEventListener('resize', close);
    document.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('resize', close); document.removeEventListener('scroll', onScroll, true); };
  }, []);
  useEffect(() => { if (!openDrill) return; const el = document.querySelector(`[data-drill="${openDrill}"]`); if (el) setPos((el as HTMLElement).getBoundingClientRect().top - 6); }, [openDrill, collapsed]);

  // ── favorites (server-backed) ──
  const favByItemId = useMemo(() => {
    const m = new Map<string, string>(); // resolved item id → the stored surface_id (may be a legacy alias)
    railPins.filter((p) => p.surface_type === 'nav').forEach((p) => m.set(resolveNavPinId(p.surface_id), p.surface_id));
    return m;
  }, [railPins]);
  const favSet = useMemo(() => new Set(favByItemId.keys()), [favByItemId]);

  const pinDescriptors: PinDescriptor[] = useMemo(() => {
    const arr: PinDescriptor[] = [];
    for (const p of railPins) {
      if (p.surface_type === 'nav') {
        const it = RAIL_ITEMS[resolveNavPinId(p.surface_id)];
        if (it) arr.push({ key: `nav:${it.id}`, itemId: it.id, isSurface: false, label: it.label, iconKey: it.iconKey, path: it.path });
      } else {
        const k = SURFACE_KIND[p.surface_type];
        if (k) arr.push({ key: `${p.surface_type}:${p.surface_id}`, isSurface: true, label: p.label, sub: k.kind, iconKey: k.icon, path: surfacePath(p), pin: p });
      }
    }
    const order = layout.item_order?.favorites ?? [];
    const keys = applyOrder(arr.map((d) => d.key), order);
    return keys.map((key) => arr.find((d) => d.key === key)!).filter(Boolean);
  }, [railPins, layout]);

  const toggleFav = (itemId: string) => {
    const it = RAIL_ITEMS[itemId];
    if (!it) return;
    if (favByItemId.has(itemId)) void unpinSurface('nav', favByItemId.get(itemId)!);
    else void pinSurface({ surface_type: 'nav', surface_id: itemId, label: it.label, parent_group: 'favorites' });
  };
  const unpinDescriptor = (d: PinDescriptor) => {
    if (d.isSurface && d.pin) void unpinSurface(d.pin.surface_type, d.pin.surface_id);
    else if (d.itemId) toggleFav(d.itemId);
  };

  // ── section order ──
  const sectionOrder = useMemo(() => {
    const known = RAIL_SECTIONS.map((s) => s.id);
    return applyOrder(known, layout.group_order ?? []);
  }, [layout.group_order]);
  const sections = useMemo(
    () => sectionOrder.map((id) => sectionById(id)).filter((s): s is RailSection => !!s && s.visible(ctx)),
    [sectionOrder, ctx],
  );
  const openSection = openDrill ? sections.find((s) => s.id === openDrill) ?? null : null;
  const openFavHere = openSection ? drillItemIds(openSection).filter((id) => favSet.has(id)) : [];

  // ── interactions ──
  const openDrillAt = (id: string, el: HTMLElement) => { setPos(el.getBoundingClientRect().top - 6); setOpenDrill((v) => (v === id ? null : id)); setTip(null); };
  const hoverLabel = (text: string | null, el?: HTMLElement) => { if (!text || !el) { setTip(null); return; } if (openDrill) return; const r = el.getBoundingClientRect(); setTip({ text, top: r.top + r.height / 2 }); };
  const navTo = (path: string) => { onNavigate(path); setOpenDrill(null); };

  // pinned reorder (persist item_order.favorites)
  const startDrag = (key: string) => () => setDrag(key);
  const overRow = (key: string) => (e: React.DragEvent) => { if (drag == null || drag === key) return; const r = e.currentTarget.getBoundingClientRect(); setDropAt({ key, before: e.clientY < r.top + r.height / 2 }); };
  const endDrag = () => {
    if (drag != null && dropAt) {
      const next = reorder(pinDescriptors.map((d) => d.key), drag, dropAt.key, dropAt.before);
      void saveRailLayout({ ...layout, item_order: { ...layout.item_order, favorites: next } });
    }
    setDrag(null); setDropAt(null);
  };
  // section reorder (persist group_order)
  const startSec = (id: string) => (e: React.DragEvent) => { setSecDrag(id); setOpenDrill(null); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; };
  const overSec = (id: string) => (e: React.DragEvent) => { if (secDrag == null || secDrag === id) return; const r = e.currentTarget.getBoundingClientRect(); setSecDrop({ id, before: e.clientY < r.top + r.height / 2 }); };
  const endSec = () => {
    if (secDrag != null && secDrop) {
      const next = reorder(sectionOrder, secDrag, secDrop.id, secDrop.before);
      void saveRailLayout({ ...layout, group_order: next });
    }
    setSecDrag(null); setSecDrop(null);
  };

  let d = 0; const nd = () => Math.min(d++ * 12, 480);
  const secDL = (id: string): 'before' | 'after' | null => (secDrag && secDrop && secDrop.id === id ? (secDrop.before ? 'before' : 'after') : null);
  const badgeFor = (b?: RailBadge): number | undefined => (b && badges ? badges[b] : undefined);

  return (
    <aside style={{ position: 'fixed', top: 0, left: 0, height: '100vh', width, flex: 'none', background: 'var(--rail-ground)', boxShadow: 'inset -1px 0 0 rgba(0,0,0,.5), 8px 0 30px -20px rgba(0,0,0,.5)', zIndex: 1201, transition: 'width 170ms cubic-bezier(.2,.7,.3,1)', display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <div className={styles.railIn} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: collapsed ? '17px 0 15px' : '18px 12px 16px 15px', justifyContent: collapsed ? 'center' : 'flex-start', borderBottom: '1px solid var(--rail-hair)' }}>
        <button type="button" onClick={collapsed ? onToggleCollapse : undefined} title={collapsed ? 'Expand rail' : undefined}
          style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 0, background: 'transparent', border: 'none', cursor: collapsed ? 'pointer' : 'default', minWidth: 0, flex: collapsed ? 'none' : 1 }}>
          <BrandMark />
          {!collapsed && (
            <span style={{ flex: 1, minWidth: 0, lineHeight: 1.15, textAlign: 'left' }}>
              <span style={{ display: 'block', fontFamily: HEAD, fontWeight: 800, fontSize: 14, letterSpacing: '-.01em', color: 'var(--rail-hi)', whiteSpace: 'nowrap' }}>The Process Lounge</span>
              <span style={{ display: 'block', fontSize: 10.5, color: 'var(--rail-faint)', letterSpacing: '.03em', marginTop: 2 }}>Black Sheep Global</span>
            </span>
          )}
        </button>
        {!collapsed && (
          <button type="button" onClick={onToggleCollapse} title="Collapse rail" style={{ flex: 'none', width: 30, height: 30, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
            <RailIcon d={RIC.collapse} size={17} color="var(--rail-faint)" />
          </button>
        )}
      </div>

      {/* nav body */}
      <nav className={styles.nav} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: collapsed ? '8px 12px 12px' : '2px 12px 12px' }}>
        {/* Pinned = favorites */}
        <PinnedLabel collapsed={collapsed} delay={nd()} />
        {pinDescriptors.length === 0 && !collapsed && (
          <div className={styles.railIn} style={{ display: 'flex', gap: 9, padding: '4px 12px 8px', animationDelay: `${nd()}ms` }}>
            <span style={{ flex: 'none', width: 18, marginTop: 1 }}><RailIcon d={RIC.star} size={15} color="var(--rail-ghost)" /></span>
            <span style={{ fontSize: 11, color: 'var(--rail-ghost)', lineHeight: 1.5 }}>Favorite any tool or open surface — the star pins it here for one-tap access.</span>
          </div>
        )}
        {pinDescriptors.map((dsc) => (
          <Row key={dsc.key} iconKey={dsc.iconKey} label={dsc.label} sub={dsc.sub} path={dsc.path} isSurface={dsc.isSurface} alert={dsc.alert}
            active={isActive(dsc.path)} collapsed={collapsed} onNav={() => navTo(dsc.path)} onHoverLabel={hoverLabel} draggable={!collapsed} grip={!collapsed} delay={nd()}
            onDragStart={startDrag(dsc.key)} onDragEnter={overRow(dsc.key)} onDragEnd={endDrag}
            dropLine={drag === dsc.key && dropAt && dropAt.key === dsc.key ? (dropAt.before ? 'before' : 'after') : null} />
        ))}

        {/* sections — user-ordered drill lead rows */}
        {sections.map((s) => (
          <DrillRow key={s.id} section={s} collapsed={collapsed} delay={nd()} count={drillCount(s, ctx)} open={openDrill === s.id}
            activeInside={drillItemIds(s).some((id) => RAIL_ITEMS[id] && RAIL_ITEMS[id].path === active?.path)}
            onOpen={openDrillAt} onHoverLabel={hoverLabel}
            secDraggable={!collapsed} onSecDragStart={startSec(s.id)} onSecDragEnter={overSec(s.id)} onSecDragEnd={endSec} secDrop={secDL(s.id)} />
        ))}
      </nav>

      {/* footer */}
      <div style={{ padding: collapsed ? '8px 12px 8px' : '6px 12px 8px', borderTop: '1px solid var(--rail-hair)' }}>
        {RAIL_FOOTER.filter((f) => !f.visible || f.visible(ctx)).map((f) => {
          // FR7/FR8: an action row opens a dialog (What's New / Submit a Request);
          // a path row navigates. Row renders a <button> when there's no path.
          const onNav = f.action === 'whatsNew' ? onWhatsNew
            : f.action === 'featureRequest' ? onFeatureRequest
            : f.action === 'devMode' ? toggleDevMode
            : f.path ? () => navTo(f.path!) : undefined;
          // Dev Mode is a STATE, not a destination — reuse the active treatment
          // so the rail shows at a glance that capture is armed, and say so in
          // the label rather than leaving the user to guess which way it toggles.
          const isDev = f.action === 'devMode';
          return (
            <Row key={f.id} iconKey={f.iconKey} label={isDev ? (devOn ? 'Dev Mode · ON' : 'Dev Mode') : f.label}
              path={f.path} badge={badgeFor(f.badge)}
              active={isDev ? devOn : (!!f.path && isActive(f.path))}
              collapsed={collapsed} onNav={onNav} onHoverLabel={hoverLabel} delay={nd()} />
          );
        })}
      </div>

      {/* profile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '10px 0' : '10px 14px', justifyContent: collapsed ? 'center' : 'flex-start', borderTop: '1px solid var(--rail-hair)' }}>
        <div style={{ width: 32, height: 32, borderRadius: '4px', background: 'var(--indigo)', color: 'rgba(255,255,255,1)', display: 'grid', placeItems: 'center', fontFamily: HEAD, fontWeight: 700, fontSize: 12.5, flex: 'none' }}>{user?.initials ?? 'MG'}</div>
        {!collapsed && (
          <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
            <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, color: 'var(--rail-hi)', whiteSpace: 'nowrap' }}>{user?.name ?? ''}</div>
            <div style={{ fontSize: 10.5, color: 'var(--rail-faint)' }}>{user?.role ?? ''}</div>
          </div>
        )}
        {!collapsed && (
          <button type="button" onClick={onLogout} title="Sign out" style={{ flex: 'none', width: 26, height: 26, borderRadius: '4px', border: 'none', background: 'transparent', cursor: onLogout ? 'pointer' : 'default', display: 'grid', placeItems: 'center' }}>
            <RailIcon d={RIC.logout} size={16} color="var(--rail-faint)" />
          </button>
        )}
      </div>

      {/* collapsed tooltip */}
      {collapsed && tip && (
        <div style={{ position: 'fixed', left: COL_W + 8, top: tip.top, transform: 'translateY(-50%)', zIndex: 1400, background: 'var(--rail-lift)', color: 'var(--rail-hi)', border: '1px solid var(--rail-hair)', borderRadius: 6, padding: '6px 11px', fontFamily: HEAD, fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap', boxShadow: 'var(--pop)', pointerEvents: 'none' }}>
          {tip.text}
        </div>
      )}

      {openSection && <DrillFlyout section={openSection} favHere={openFavHere} pos={pos} railW={width} activePath={activePath} favSet={favSet} navCtx={ctx}
        onNav={(it) => navTo(it.path)} onToggleFav={toggleFav} onClose={() => setOpenDrill(null)} />}
    </aside>
  );
}
